import { Divider, SegmentedControl, Switch, Badge } from '@mantine/core'
import { AppStack, AppText } from '../../app/common/ui'
import { useFeatureFlag } from '../../app/hooks/useFeatureFlag'
import BuildCollaboratorsSection from './BuildCollaboratorsSection'

export type BuildVisibilityValue = 'Private' | 'Unlisted' | 'Public'

type Props = {
  buildId?: string
  canManageCollaborators?: boolean
  visibility: BuildVisibilityValue
  allowDuplication: boolean
  onVisibilityChange: (value: BuildVisibilityValue) => void
  onAllowDuplicationChange: (value: boolean) => void
  disabled?: boolean
}

const VISIBILITY_OPTIONS: { value: BuildVisibilityValue; label: string }[] = [
  { value: 'Private', label: 'Private' },
  { value: 'Unlisted', label: 'Unlisted' },
  { value: 'Public', label: 'Public' }
]

export default function BuildSettingsSections ({
  buildId,
  canManageCollaborators = false,
  visibility,
  allowDuplication,
  onVisibilityChange,
  onAllowDuplicationChange,
  disabled = false
}: Props) {
  const privacyOn = useFeatureFlag('builds-privacy-collaboration-enabled')

  return (
    <AppStack spacing="md" mt="lg">
      <Divider label="Share" labelPosition="left" />
      {!privacyOn
        ? (
          <>
            <AppText size="sm" color="dimmed">
              Visibility controls activate when build privacy is enabled on this environment (Admin → Feature flags → Builds).
            </AppText>
            <Badge variant="outline" color="gray">Privacy enforcement off</Badge>
          </>
          )
        : (
          <>
            <AppText size="sm" color="dimmed">
              Private builds are only visible to you (and collaborators when that ships). Unlisted builds work via direct link but do not appear in search or community lists.
            </AppText>
            <SegmentedControl
              fullWidth
              value={visibility}
              onChange={(v) => onVisibilityChange(v as BuildVisibilityValue)}
              data={VISIBILITY_OPTIONS}
              disabled={disabled}
            />
            {visibility === 'Public' && (
              <Switch
                label="Allow others to duplicate this build"
                description="When on, logged-in users can copy this public build to their account (coming in a later release)."
                checked={allowDuplication}
                onChange={(e) => onAllowDuplicationChange(e.currentTarget.checked)}
                disabled={disabled}
              />
            )}
          </>
          )}

      <Divider label="Collaborators" labelPosition="left" />
      {buildId
        ? (
          <BuildCollaboratorsSection buildId={buildId} canManage={canManageCollaborators} />
          )
        : (
          <AppText size="sm" color="dimmed">Save the build before inviting collaborators.</AppText>
          )}
    </AppStack>
  )
}
